<template>
  <div class="expenses-report">
    <!-- ════ HEADER + SELECTOR DE PERÍODO ════ -->
    <div class="er-header">
      <div class="er-header__left">
        <h6 class="er-header__title">Reportería de Gastos</h6>
        <small class="er-header__sub">{{ summary.period_label }}</small>
      </div>
      <div class="er-header__controls">
        <!-- Período -->
        <div class="er-period-pills">
          <button
            v-for="p in periodOptions"
            :key="p.value"
            class="er-period-pill"
            :class="{ active: period === p.value }"
            @click="
              period = p.value;
              loadReport();
            "
          >
            {{ p.label }}
          </button>
        </div>
        <!-- Selector de año / mes según período -->
        <div class="er-date-selects">
          <select
            v-model.number="selectedYear"
            @change="loadReport"
            class="er-select"
          >
            <option v-for="y in yearOptions" :key="y" :value="y">{{
              y
            }}</option>
          </select>
          <select
            v-if="period === 'month'"
            v-model.number="selectedMonth"
            @change="loadReport"
            class="er-select"
          >
            <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">{{
              m
            }}</option>
          </select>
          <select
            v-if="period === 'quarter'"
            v-model.number="selectedQuarterMonth"
            @change="loadReport"
            class="er-select"
          >
            <option :value="1">Q1</option>
            <option :value="4">Q2</option>
            <option :value="7">Q3</option>
            <option :value="10">Q4</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="er-loading">
      <i class="el-icon-loading er-loading__icon"></i>
      <span>Cargando datos...</span>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="er-empty">
      <i class="el-icon-warning-outline er-empty__icon"></i>
      <p class="er-empty__title">No se pudo cargar el reporte</p>
      <p class="er-empty__sub">Verifica tu conexión e intenta nuevamente</p>
      <button class="er-empty__btn" @click="loadReport">Reintentar</button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="summary.total === 0 && byCategory.length === 0"
      class="er-empty"
    >
      <i class="el-icon-document er-empty__icon"></i>
      <p class="er-empty__title">Sin gastos en este período</p>
      <p class="er-empty__sub">
        No se encontraron registros para el rango seleccionado
      </p>
    </div>

    <template v-else>
      <!-- ════ FILA 1: KPIs ════ -->
      <div class="er-kpi-row">
        <KpiSecondaryCard
          label="Total gastos"
          :value="summary.total"
          prefix="$"
          icon="el-icon-s-finance"
          icon-color="#2d2b55"
          icon-bg="#eeedfa"
          :change="
            summary.change_vs_prev !== null
              ? Math.abs(summary.change_vs_prev)
              : null
          "
          :direction="
            summary.change_vs_prev > 0
              ? 'up'
              : summary.change_vs_prev < 0
              ? 'down'
              : 'neutral'
          "
          :subtext-line="
            summary.change_vs_prev !== null
              ? summary.change_vs_prev > 0
                ? 'Más que período anterior'
                : 'Menos que período anterior'
              : ''
          "
        />
        <KpiSecondaryCard
          label="Promedio mensual (6m)"
          :value="summary.avg_monthly"
          prefix="$"
          icon="el-icon-data-analysis"
          icon-color="#3ecfb4"
          icon-bg="#e8f8f4"
        />
        <KpiSecondaryCard
          label="Gasto más alto"
          :value="summary.top_provider ? summary.top_provider.total : null"
          prefix="$"
          icon="el-icon-trophy"
          icon-color="#e07c2a"
          icon-bg="#fff3e8"
          :subtext-line="summary.top_provider ? summary.top_provider.name : ''"
        />
        <KpiSecondaryCard
          label="Total registros"
          :value="detail.length"
          prefix=""
          icon="el-icon-document"
          icon-color="#3b6fd4"
          icon-bg="#e8f0ff"
        />
      </div>

      <!-- ════ FILA 2: Por categoría + Top proveedores ════ -->
      <div class="er-two-col">
        <!-- Por categoría -->
        <div class="report-card">
          <div class="report-card-header">
            <h6 class="report-card-title">Gastos por categoría</h6>
          </div>
          <div v-if="byCategory.length === 0" class="er-empty-inline">
            Sin datos
          </div>
          <div v-else class="er-category-list">
            <div v-for="cat in byCategory" :key="cat.key" class="er-cat-row">
              <div class="er-cat-row__top">
                <span class="er-cat-row__name">{{ cat.name }}</span>
                <span class="er-cat-row__meta"
                  >{{ cat.count }} gasto{{ cat.count !== 1 ? "s" : "" }} ·
                  {{ cat.percentage }}%</span
                >
                <span class="er-cat-row__amount"
                  >${{ formatAmount(cat.total) }}</span
                >
              </div>
              <div class="er-cat-bar">
                <div
                  class="er-cat-bar__fill"
                  :class="'er-cat-bar--' + cat.key"
                  :style="{ width: cat.percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Top proveedores -->
        <div class="report-card">
          <div class="report-card-header">
            <h6 class="report-card-title">Top proveedores</h6>
          </div>
          <div v-if="topProviders.length === 0" class="er-empty-inline">
            Sin datos
          </div>
          <div v-else class="er-providers-list">
            <div
              v-for="(p, idx) in topProviders"
              :key="idx"
              class="er-provider-row"
            >
              <span class="er-provider-rank">{{ idx + 1 }}</span>
              <div class="er-provider-avatar">{{ p.initials }}</div>
              <div class="er-provider-info">
                <span class="er-provider-name">{{ p.name }}</span>
                <span class="er-provider-sub"
                  >{{ p.invoice_count }} registro{{
                    p.invoice_count !== 1 ? "s" : ""
                  }}</span
                >
              </div>
              <span class="er-provider-total"
                >${{ formatAmount(p.total) }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- ════ FILA 3: Evolución mensual ════ -->
      <div class="report-card er-chart-card">
        <div class="report-card-header">
          <h6 class="report-card-title">
            Evolución de gastos — últimos 6 meses
          </h6>
        </div>
        <div class="er-chart-wrap">
          <BarExpenseEvolution
            v-if="chartReady"
            :chart-data="chartData"
            :key="chartKey"
            :height="280"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/config/Api";
import KpiSecondaryCard from "./KpiSecondaryCard";
import BarExpenseEvolution from "./BarExpenseEvolution";

export default {
  name: "ExpensesReport",
  components: { KpiSecondaryCard, BarExpenseEvolution },

  data() {
    const now = new Date();
    return {
      loading: true,
      period: "month",
      selectedYear: now.getFullYear(),
      selectedMonth: now.getMonth() + 1,
      selectedQuarterMonth: Math.ceil((now.getMonth() + 1) / 3) * 3 - 2,
      chartReady: false,
      chartKey: 0,

      error: false,

      // Data del API
      summary: {
        total: 0,
        avg_monthly: 0,
        top_provider: null,
        change_vs_prev: null,
        period_label: ""
      },
      byCategory: [],
      topProviders: [],
      monthlyEvolution: [],
      detail: [],

      periodOptions: [
        { value: "month", label: "Mes" },
        { value: "quarter", label: "Trimestre" },
        { value: "year", label: "Año" }
      ],
      monthNames: [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre"
      ]
    };
  },

  computed: {
    businessId() {
      return this.$store.getters.getUser?.business_id;
    },

    yearOptions() {
      const y = new Date().getFullYear();
      return [y, y - 1, y - 2, y - 3];
    },

    chartData() {
      return {
        labels: this.monthlyEvolution.map(m => m.month),
        values: this.monthlyEvolution.map(m => m.total)
      };
    }
  },

  mounted() {
    this.loadReport();
  },

  methods: {
    async loadReport() {
      this.loading = true;
      this.chartReady = false;

      const month =
        this.period === "quarter"
          ? this.selectedQuarterMonth
          : this.selectedMonth;

      this.error = false;

      try {
        const res = await Api().get("/reports/expenses", {
          params: {
            business_id: this.businessId,
            period: this.period,
            year: this.selectedYear,
            month
          }
        });

        if (res?.data?.success) {
          const d = res.data.data;
          this.summary = d.summary;
          this.byCategory = d.by_category || [];
          this.topProviders = d.top_providers || [];
          this.monthlyEvolution = d.monthly_evolution || [];
          this.detail = d.detail || [];

          this.$nextTick(() => {
            this.chartReady = true;
            this.chartKey++;
          });
        }
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    formatAmount(val) {
      if (val === null || val === undefined) return "0.00";
      return parseFloat(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$dark: #2d2b55;
$teal: #3ecfb4;
$grey: #909399;
$border: #e4eaf6;

// ── Layout ───────────────────────────────────────────────
.expenses-report {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ── Header ───────────────────────────────────────────────
.er-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: $dark;
    margin: 0;
  }
  &__sub {
    font-size: 12px;
    color: $grey;
    display: block;
    margin-top: 2px;
  }
  &__controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.er-period-pills {
  display: flex;
  border: 1px solid $border;
  border-radius: 8px;
  overflow: hidden;
}
.er-period-pill {
  padding: 5px 13px;
  font-size: 12px;
  font-weight: 500;
  color: $grey;
  background: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  & + .er-period-pill {
    border-left: 1px solid $border;
  }
  &:hover {
    color: $dark;
    background: #f7f8f9;
  }
  &.active {
    background: $dark;
    color: #fff;
    font-weight: 600;
  }
}

.er-date-selects {
  display: flex;
  gap: 6px;
}
.er-select {
  padding: 5px 10px;
  border: 1px solid $border;
  border-radius: 8px;
  font-size: 12px;
  color: $dark;
  background: #fff;
  cursor: pointer;
  outline: none;
  &:focus {
    border-color: $teal;
  }
}

// ── Loading ──────────────────────────────────────────────
.er-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 60px 20px;
  color: $grey;
  font-size: 14px;
  &__icon {
    font-size: 22px;
    color: $teal;
  }
}

// ── Empty / Error state ──────────────────────────────────
.er-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 64px 20px;
  text-align: center;

  &__icon {
    font-size: 44px;
    color: #d0d6e8;
    margin-bottom: 8px;
  }
  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $dark;
    margin: 0;
  }
  &__sub {
    font-size: 13px;
    color: $grey;
    margin: 0;
  }
  &__btn {
    margin-top: 12px;
    padding: 7px 20px;
    border: 1.5px solid $border;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: $dark;
    cursor: pointer;
    transition: all 0.15s;
    &:hover {
      border-color: $teal;
      color: $teal;
    }
  }
}

// ── Report card (reutiliza el estilo del Main.vue) ────────
.report-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 16px 18px;
}
.report-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.report-card-title {
  font-size: 15px;
  font-weight: 500;
  color: $dark;
  margin: 0;
}

.er-total-count {
  font-size: 12px;
  color: $grey;
}
.er-empty-inline {
  font-size: 13px;
  color: $grey;
  padding: 12px 0;
  text-align: center;
}

// ── KPI row ──────────────────────────────────────────────
.er-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

// ── Two column layout ────────────────────────────────────
.er-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// ── Categorías ───────────────────────────────────────────
.er-category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.er-cat-row {
  &__top {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 4px;
  }
  &__name {
    font-size: 13px;
    font-weight: 500;
    color: $dark;
    flex: 1;
  }
  &__meta {
    font-size: 11px;
    color: $grey;
  }
  &__amount {
    font-size: 13px;
    font-weight: 600;
    color: $dark;
    flex-shrink: 0;
  }
}

.er-cat-bar {
  height: 5px;
  background: #f0f2f7;
  border-radius: 3px;
  overflow: hidden;
  &__fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.5s ease;
  }
}

// Colores de barras por categoría
.er-cat-bar--software {
  background: #7c5cbf;
}
.er-cat-bar--accounting {
  background: #3b6fd4;
}
.er-cat-bar--materials {
  background: #e07c2a;
}
.er-cat-bar--subcontract {
  background: #c0445a;
}
.er-cat-bar--hosting {
  background: #5c6bc0;
}
.er-cat-bar--travel {
  background: #c9a400;
}
.er-cat-bar--other {
  background: #888;
}

// ── Top proveedores ──────────────────────────────────────
.er-providers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.er-provider-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f5f7fb;
  &:last-child {
    border-bottom: none;
  }
}

.er-provider-rank {
  font-size: 12px;
  font-weight: 700;
  color: $grey;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}
.er-provider-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #eeedfa;
  color: $dark;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.er-provider-info {
  flex: 1;
  min-width: 0;
}
.er-provider-name {
  font-size: 13px;
  font-weight: 500;
  color: $dark;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
.er-provider-sub {
  font-size: 11px;
  color: $grey;
}
.er-provider-total {
  font-size: 13px;
  font-weight: 600;
  color: $dark;
  flex-shrink: 0;
}

// ── Chart ────────────────────────────────────────────────
.er-chart-card {
  overflow: hidden;
}
.er-chart-wrap {
  height: 280px;
  position: relative;
  overflow: hidden;

  // Contiene el canvas de vue-chartjs
  > div {
    height: 100% !important;
    width: 100% !important;
  }

  canvas {
    height: 100% !important;
    max-height: 280px;
  }
}
</style>
